// Protokoll zwischen Web-DAW (Browser) und SRS Studio Bridge (native App, C++/JUCE).
// Transport: WebSocket auf ws://127.0.0.1:47800, Nachrichten als JSON-Text.
// Jede Nachricht hat { type, id? }. Antworten auf Anfragen tragen dieselbe id.
// Die C++-Seite (apps/bridge/Source/Protocol.h) spiegelt diese Typen 1:1.

export const BRIDGE_PORT = 47800;
export const BRIDGE_PROTOCOL_VERSION = 1;

export interface DeviceInfo {
  id: string;            // z. B. "CoreAudio:RME Digiface Dante"
  name: string;
  type: string;          // "CoreAudio" | "ASIO" | "ALSA" …
  inputs: number;
  outputs: number;
  sampleRates: number[];
  bufferSizes: number[];
}

export interface ChannelInfo { channel: number; name: string }

export interface DeviceState {
  deviceId: string;
  name: string;
  sampleRate: number;
  bufferSize: number;
  inputs: ChannelInfo[];
  outputs: ChannelInfo[];
  inputLatencySamples: number;
  outputLatencySamples: number;
  /** per Loopback gemessene Zusatzlatenz (wird der Aufnahme zugerechnet), ab Bridge 0.2 */
  extraLatencySamples?: number;
  /** gespeicherter Roundtrip der letzten Messung für dieses Gerät, 0 = noch nicht gemessen */
  measuredRoundTripSamples?: number;
}

export interface PluginInfo {
  id: string;            // eindeutiger Bezeichner aus dem Plugin-Scan
  name: string;
  vendor: string;
  format: 'VST3' | 'AU';
  isInstrument: boolean;
}

export interface RecordedFile {
  channel: number;
  trackId: string;
  path: string;          // lokaler Pfad auf dem Rechner der Bridge
  sampleRate: number;
  frames: number;
  /** Samples, um die die Aufnahme nach vorn geschoben werden muss (Latenzausgleich) */
  offsetSamples: number;
}

export interface MidiNoteMsg { t: number; d: number; m: number; v: number }

/** Eigenes Plugin in einer Kette: Zustand (base64) und Regler nach Index ("p12" → 0..1). Erst Zustand, dann Regler. */
export interface ChainSlotMsg { pluginId: string; state?: string; params: Record<string, number>; bypass?: boolean }

/** Regler eines Plugins; texts = Anzeige an gleichmäßig verteilten Stellen 0..1 (für die Zuordnung) */
export interface PluginParamInfo { i: number; name: string; label: string; steps: number; value: number; text: string; auto: boolean; texts: string[] }

// ---------- Binärnachrichten (Audio, Plugin-Fenster), ab Bridge 0.3 ----------
// 48 Byte Kopf (little-endian): "SRSB", kind, Kanäle, Slot, frei, a (u32), b (u32), Schlüssel (32 Byte ASCII)
// kind 1 Audio-Eingang, 2 gerechnetes Audio, 3 Live-Audio: a = erster Frame, b = Frames, danach float32 planar
// kind 4 Plugin-Fenster: a/b = Breite/Höhe in Punkten, danach JPEG
export const BIN_HEADER = 48;
export const BIN = { audioIn: 1, renderOut: 2, liveOut: 3, editorFrame: 4 } as const;
export interface BinHeader { kind: number; channels: number; slot: number; a: number; b: number; key: string }

export function makeBin(h: BinHeader, payloadBytes: number): { buf: ArrayBuffer; payload: Uint8Array } {
  const buf = new ArrayBuffer(BIN_HEADER + payloadBytes);
  const u8 = new Uint8Array(buf), dv = new DataView(buf);
  u8.set([83, 82, 83, 66], 0);
  u8[4] = h.kind; u8[5] = h.channels; u8[6] = h.slot;
  dv.setUint32(8, h.a >>> 0, true); dv.setUint32(12, h.b >>> 0, true);
  for (let i = 0; i < Math.min(32, h.key.length); i++) u8[16 + i] = h.key.charCodeAt(i) & 0x7f;
  return { buf, payload: new Uint8Array(buf, BIN_HEADER) };
}

export function parseBin(buf: ArrayBuffer): (BinHeader & { data: ArrayBuffer }) | null {
  if (buf.byteLength < BIN_HEADER) return null;
  const u8 = new Uint8Array(buf), dv = new DataView(buf);
  if (u8[0] !== 83 || u8[1] !== 82 || u8[2] !== 83 || u8[3] !== 66) return null;
  let key = '';
  for (let i = 16; i < 48 && u8[i]; i++) key += String.fromCharCode(u8[i]);
  return { kind: u8[4], channels: u8[5], slot: u8[6], a: dv.getUint32(8, true), b: dv.getUint32(12, true), key, data: buf.slice(BIN_HEADER) };
}

// ---------- Browser -> Bridge ----------
export type ToBridge =
  | { type: 'hello'; id: string; app: 'srs-studio'; protocol: number; pairingCode?: string }
  | { type: 'listDevices'; id: string }
  | { type: 'openDevice'; id: string; deviceId: string; sampleRate: number; bufferSize: number }
  | { type: 'subscribeMeters'; id: string; channels: number[]; fps: number }
  | { type: 'recordStart'; id: string; takeId: string; inputs: { channel: number; trackId: string }[] }
  | { type: 'recordStop'; id: string; takeId: string }
  | { type: 'uploadTake'; id: string; takeId: string; uploadUrl: string; authToken: string; projectId?: string }
  | { type: 'listPlugins'; id: string; rescan?: boolean }
  | { type: 'renderMidi'; id: string; jobId: string; pluginId: string; preset?: string; bpm: number; lengthBeats: number; notes: MidiNoteMsg[]; sampleRate: number }
  | { type: 'setPluginParams'; id: string; trackId: string; slot: string; pluginId: string; params: Record<string, number> }
  | { type: 'measureLatency'; id: string; outputChannel: number; inputChannel: number }
  // eigene Effekt-Plugins (ab Bridge 0.3)
  | { type: 'describePlugin'; id: string; pluginId: string; samples?: number }
  | { type: 'inputBegin'; id: string; inputKey: string; channels: number; frames: number; sampleRate: number }
  | { type: 'renderAudio'; id: string; jobId: string; inputKey: string; chain: ChainSlotMsg[]; tail: number; bpm?: number }
  | { type: 'liveOpen'; id: string; chainId: string; inputKey: string; chain: ChainSlotMsg[]; bpm?: number }
  | { type: 'liveParams'; id: string; chainId: string; slot: number; params: Record<string, number>; state?: string }
  | { type: 'liveProcess'; id: string; chainId: string; from: number; frames: number }
  | { type: 'liveClose'; id: string; chainId: string }
  | { type: 'openEditor'; id: string; chainId: string; slot: number }
  | { type: 'closeEditor'; id: string; chainId: string; slot: number }
  | { type: 'editorInput'; id: string; chainId: string; slot: number; kind: 'down' | 'up' | 'move' | 'wheel' | 'key'; x: number; y: number; button?: number; mods?: number; dx?: number; dy?: number; key?: string; keyDown?: boolean };

// ---------- Bridge -> Browser ----------
export type FromBridge =
  | { type: 'helloAck'; id: string; bridgeVersion: string; protocol: number; os: string; paired: boolean }
  | { type: 'devices'; id: string; devices: DeviceInfo[] }
  | { type: 'deviceState'; id?: string; state: DeviceState }
  | { type: 'meters'; levels: number[]; peak: number[] }
  | { type: 'recordStarted'; id: string; takeId: string; startedAt: number }
  | { type: 'recordStopped'; id: string; takeId: string; files: RecordedFile[] }
  | { type: 'uploadProgress'; takeId: string; channel: number; percent: number }
  | { type: 'uploadDone'; id: string; takeId: string; channel: number; sourceId: string }
  | { type: 'plugins'; id: string; plugins: PluginInfo[] }
  | { type: 'renderProgress'; jobId: string; percent: number }
  | { type: 'renderDone'; id: string; jobId: string; path: string; frames: number; sampleRate: number }
  | { type: 'latency'; id: string; roundTripSamples: number; roundTripMs: number }
  | { type: 'error'; id?: string; code: string; message: string }
  | { type: 'pluginDescription'; id: string; pluginId: string; name: string; vendor: string; latency: number; tail: number; params: PluginParamInfo[] }
  | { type: 'inputStored'; id: string; inputKey: string }
  | { type: 'renderAudioDone'; id: string; jobId: string; frames: number; channels: number; sampleRate: number; latency: number }
  | { type: 'liveReady'; id: string; chainId: string; latency: number }
  | { type: 'pluginChanged'; chainId: string; slot: number; params: Record<string, number>; texts: Record<string, string>; state?: string }
  | { type: 'editorOpened'; id: string; chainId: string; slot: number; width: number; height: number }
  | { type: 'editorClosed'; chainId: string; slot: number };

export function isFromBridge(x: unknown): x is FromBridge {
  return typeof x === 'object' && x !== null && typeof (x as { type?: unknown }).type === 'string';
}
